import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { motion } from 'framer-motion';
import { Copy, FileCheck, GitBranch } from 'lucide-react';
import { toast } from 'sonner';

const SEV_COLOR = {
  critical: 'text-apex-critical bg-red-50 border-red-200',
  high:     'text-amber-700 bg-amber-50 border-amber-200',
  medium:   'text-sky-700 bg-sky-50 border-sky-200',
};

export default function EdgeEvidenceDialog({ edge, open, onOpenChange }) {
  if (!edge) return null;
  const sev = SEV_COLOR[edge.severity] ?? SEV_COLOR.medium;
  const copyHash = () => {
    try {
      navigator.clipboard.writeText(edge.evidence_hash);
      toast.success('Evidence hash copied');
    } catch { toast.error('Could not copy'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edge-evidence-dialog" className="sm:max-w-[680px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold tracking-tight text-apex-ink flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-apex-lens" />
            {edge.label}
            <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold border rounded px-1.5 py-0.5 ${sev}`}>
              {edge.severity}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-apex-neutral font-mono">
            {edge.meta}
          </DialogDescription>
        </DialogHeader>

        {/* Summary */}
        <div className="text-[12px] text-apex-ink-2 leading-relaxed bg-slate-50 border border-slate-200 rounded-md p-3 mt-1">
          {edge.summary}
        </div>

        {/* Events table */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] uppercase tracking-wider font-semibold text-apex-neutral">CloudTrail evidence · {edge.events.length} events</h4>
            <span className="text-[10px] font-mono text-apex-neutral">read-only · S3 Object Lock</span>
          </div>
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-apex-neutral">
                <tr>
                  <th className="text-left px-2.5 py-1.5 font-semibold">Timestamp (UTC)</th>
                  <th className="text-left px-2.5 py-1.5 font-semibold">Principal</th>
                  <th className="text-left px-2.5 py-1.5 font-semibold">Action</th>
                  <th className="text-left px-2.5 py-1.5 font-semibold hidden sm:table-cell">Source IP</th>
                  <th className="text-right px-2.5 py-1.5 font-semibold">Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-mono">
                {edge.events.map((e, i) => (
                  <motion.tr
                    key={e.evidence_id}
                    initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    data-testid={`edge-event-${e.evidence_id}`}
                    className="hover:bg-slate-50/60"
                  >
                    <td className="px-2.5 py-1.5 text-apex-ink-2 whitespace-nowrap">{e.ts}</td>
                    <td className="px-2.5 py-1.5 text-apex-ink truncate max-w-[160px]" title={e.principal}>{e.principal}</td>
                    <td className="px-2.5 py-1.5 text-apex-lens">{e.action}</td>
                    <td className="px-2.5 py-1.5 text-apex-neutral hidden sm:table-cell">{e.source_ip}</td>
                    <td className="px-2.5 py-1.5 text-right text-apex-neutral">{e.evidence_id}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Hash + provenance footer */}
        <div className="mt-3 rounded-md bg-apex-shell text-slate-300 px-3 py-2.5 text-[11px] font-mono">
          <div className="flex items-center justify-between gap-2">
            <span className="text-slate-400 inline-flex items-center gap-1.5"><FileCheck className="w-3 h-3 text-apex-lens" />Bundle hash</span>
            <span className="text-apex-lens truncate">{edge.evidence_hash}</span>
            <button onClick={copyHash} data-testid="copy-edge-hash" className="text-slate-400 hover:text-white shrink-0">
              <Copy className="w-3 h-3" />
            </button>
          </div>
          <div className="mt-1 flex items-center justify-between text-[10px] text-slate-500">
            <span>Bundle ID</span>
            <span>{edge.evidence_id}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
